import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { IoRefreshOutline } from 'react-icons/io5';
import { useSearchParams } from 'react-router-dom';

import { Table } from 'src/components/TableNew';
import NoResult from 'src/components/NoResult/NoResult';
import { FilterBar, type FilterDef } from 'src/components/FilterBar';
import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import {
  ActivityLog,
  ActorType,
  LogSource,
  dateInputToIso,
  fetchActivityLogs,
  formatActorTypeLabel,
  formatDurationMs,
  formatSourceLabel,
  statusTone,
} from 'src/api/adminLogs';
import { convertDate } from 'src/components/Table/helpers';
import { useFilterQueryEffect, type FilterQueryParams } from 'src/hooks/useFilterQueryEffect';

import LogDetailModal from './LogDetailModal';
import {
  ActorCell,
  ActorEmail,
  DurationText,
  SpinIcon,
  StatusCode,
  ToolButton,
  ToolbarActionButtons,
  ToolbarActions,
  ToolbarRow,
  TypeBadge,
} from './styled';

const ACTOR_TYPE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'customer', label: 'Customer' },
  { value: 'system', label: 'System' },
  { value: 'anonymous', label: 'Anonymous' },
];

const SOURCE_OPTIONS = [
  { value: 'cloud', label: 'Cloud' },
  { value: 'local', label: 'Local' },
];

// The API wants a whole HTTP status (100-599); a picker of the ones the
// platform actually returns means no half-typed "20" ever reaches it.
const STATUS_OPTIONS = ['200', '201', '204', '400', '401', '403', '404', '409', '422', '429', '500', '502', '503'].map(
  (code) => ({ value: code, label: code }),
);

// One bar per filter; the API ANDs them. `actor` is a person: an id (from the
// ?actor= deep link) is sent as the exact `actor`, anything else as the
// email / name prefix `actor_search`.
const ACTIVITY_FILTERS: FilterDef[] = [
  { key: 'actor', label: 'Actor', placeholder: 'Email, name or id' },
  { key: 'action', label: 'Action', placeholder: 'e.g. POST /v1/admin' },
  { key: 'actor_type', label: 'Actor type', options: ACTOR_TYPE_OPTIONS, allLabel: 'All actors' },
  { key: 'source', label: 'Source', options: SOURCE_OPTIONS, allLabel: 'All sources' },
  { key: 'status', label: 'Status', options: STATUS_OPTIONS, allLabel: 'All statuses' },
  { key: 'from', label: 'From', type: 'date' },
  { key: 'to', label: 'To', type: 'date' },
];

const OBJECT_ID = /^[0-9a-f]{24}$/i;

const HEADERS = ['Time', 'Actor', 'Action', 'Status', 'Duration'];
const POLL_MS = 45_000;

type ActivityTabProps = {
  onTotalChange?: Dispatch<SetStateAction<number | null>>;
};

const ActivityTab: React.FC<ActivityTabProps> = ({ onTotalChange }) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const actorParam = (searchParams.get('actor') || '').trim();

  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>(() =>
    actorParam ? { actor: actorParam } : ({} as Record<string, string>),
  );
  const [selected, setSelected] = useState<ActivityLog | null>(null);
  // The last query the hook ran, so polling and the refresh button repeat it.
  const lastQueryRef = useRef<FilterQueryParams>({ page: 1, filters: {} });

  // The ?actor= deep link pre-fills the Actor filter (user pages link here).
  useEffect(() => {
    if (!actorParam) return;
    setFilters((prev) => (prev.actor === actorParam ? prev : { ...prev, actor: actorParam }));
  }, [actorParam]);

  const clearActorParam = useCallback(() => {
    if (!searchParams.has('actor')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('actor');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const load = useCallback(
    async (params: FilterQueryParams, silent = false) => {
      lastQueryRef.current = params;
      const { actor = '', action, actor_type, source, status, from = '', to = '' } = params.filters;
      const actorIsId = OBJECT_ID.test(actor);
      if (!silent) setLoading(true);
      try {
        const res = await fetchActivityLogs({
          page: params.page,
          limit: 20,
          actor_type: (actor_type as ActorType) || undefined,
          actor: actorIsId ? actor : undefined,
          actor_search: actor && !actorIsId ? actor : undefined,
          action: action || undefined,
          status: status || undefined,
          source: (source as LogSource) || undefined,
          from: dateInputToIso(from),
          to: dateInputToIso(to, true),
        });
        setLogs(res.activity_logs);
        setTotalPages(Math.max(1, res.meta?.page_count || 1));
        onTotalChange?.(res.meta?.total_count || 0);
      } catch (e: any) {
        if (!silent) {
          dispatch(
            setAppAlert({
              message: e?.response?.data?.error ?? 'Failed to load activity logs',
              isSuccess: false,
            }),
          );
          setLogs([]);
          onTotalChange?.(0);
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [dispatch, onTotalChange],
  );

  const { refresh: refreshQuery } = useFilterQueryEffect({ filters, currentPage, setCurrentPage, query: load });

  useEffect(() => {
    const id = window.setInterval(() => load(lastQueryRef.current, true), POLL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    // Editing the pre-filled actor is leaving the deep link behind, so the URL
    // stops re-applying it.
    if (key === 'actor' && actorParam && value !== actorParam) clearActorParam();
  };

  const handleClear = () => {
    setFilters({});
    clearActorParam();
  };

  const rows = useMemo(
    () =>
      logs.map((log) => ({
        ...log,
        time_display: log.created_at
          ? `${convertDate(log.created_at).date} ${convertDate(log.created_at).time}`
          : '—',
        actor_display: log.actor_email || formatActorTypeLabel(log.actor_type),
        action_display: log.action || '—',
        status_display: String(log.status ?? '—'),
        duration_display: formatDurationMs(log.duration_ms),
      })),
    [logs],
  );

  return (
    <>
      <FilterBar
        filters={ACTIVITY_FILTERS}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClear}
        onRefresh={refreshQuery}
      />
      <ToolbarRow $flush>
        <ToolbarActions>
          <ToolbarActionButtons>
            <ToolButton
              type="button"
              $iconOnly
              disabled={loading}
              onClick={() => load(lastQueryRef.current)}
              title="Refresh logs"
              aria-label="Refresh logs"
            >
              <SpinIcon $spin={loading}>
                <IoRefreshOutline size={18} />
              </SpinIcon>
            </ToolButton>
          </ToolbarActionButtons>
        </ToolbarActions>
      </ToolbarRow>

      {rows.length > 0 || loading ? (
        <Table
          loading={loading}
          headers={HEADERS}
          items={rows}
          columns="1.1fr 1.2fr 1.8fr 0.6fr 0.7fr"
          fieldsToShow={['time_display', 'actor_display', 'action_display', 'status_display', 'duration_display']}
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          disableRowDetails
          onRowClick={(item) => setSelected(item as ActivityLog)}
          renderCell={(item, fieldName) => {
            if (fieldName === 'actor_display') {
              return (
                <ActorCell>
                  <ActorEmail title={item.actor_email || undefined}>{item.actor_display}</ActorEmail>
                  <TypeBadge>{formatActorTypeLabel(item.actor_type)}</TypeBadge>
                  {item.source ? <TypeBadge>{formatSourceLabel(item.source)}</TypeBadge> : null}
                </ActorCell>
              );
            }
            if (fieldName === 'status_display') {
              return <StatusCode $tone={statusTone(item.status)}>{item.status ?? '—'}</StatusCode>;
            }
            if (fieldName === 'duration_display') {
              return <DurationText $tone="neutral">{item.duration_display}</DurationText>;
            }
            if (fieldName === 'action_display') {
              return <span title={item.action}>{item.action_display}</span>;
            }
            return null;
          }}
        />
      ) : (
        <NoResult />
      )}

      {selected && (
        <LogDetailModal kind="activity" log={selected} onClose={() => setSelected(null)} />
      )}
    </>
  );
};

export default ActivityTab;
